import Service from './Service';
import LogicBaseLiveStreamApi from './server/LogicBaseLiveStreamApi';

/** Per-content live stream doc fetched from API. */
class LiveStreamSession extends Service {
  streams = {};

  /** Per-content handlers for `livestream-camctrl` websocket pushes. */
  camControlListeners = new Map();

  /** Last query (e.g. password) used to load each content, for realtime re-fetch. */
  queries = {};

  reloadTimers = {};

  /** In-flight `load` promises so duplicate GETs share one request. */
  loadPromises = {};

  async load(contentId, query) {
    if (!contentId) return null;
    const key = String(contentId);
    if (query !== undefined) this.queries[key] = query;
    if (this.loadPromises[key]) return this.loadPromises[key];

    const previous = this.streams[key];
    this.loadPromises[key] = (async () => {
      try {
        const doc = await LogicBaseLiveStreamApi.getForContent(key, query ?? this.queries[key]);
        // Server.__request returns null for a duplicate in-flight GET — do not
        // treat that as NotFound or the host stays off the mode picker.
        if (doc == null) {
          if (previous !== undefined) return previous;
          this.scheduleReload(key);
          return undefined;
        }
        if (doc.error || doc.NotFound) {
          // Replica lag right after create: keep a seeded doc and retry.
          if (previous && !previous.error) {
            this.scheduleReload(key);
            return previous;
          }
          this.streams[key] = null;
        } else {
          this.streams[key] = doc;
        }
        this.emitStateUpdate();
        return this.streams[key];
      } catch {
        // Keep the last good doc on poll/network errors so playback does not flash TreeAnswerPage.
        if (previous !== undefined) return previous;
        this.scheduleReload(key);
        return undefined;
      } finally {
        delete this.loadPromises[key];
      }
    })();
    return this.loadPromises[key];
  }

  get(contentId) {
    return this.streams[String(contentId)] ?? undefined;
  }

  setLocal(contentId, doc) {
    this.streams[String(contentId)] = doc;
    this.emitStateUpdate();
  }

  /**
   * Apply a realtime `livestream-update` websocket push: merge the viewer-safe
   * fields (status flip, timeline, accessMode, presenter set) for an instant UI
   * reaction, then re-fetch the authoritative per-account shape on a short
   * debounce. Playback/replay URLs are not in the push (shared content room);
   * they arrive only from the re-fetch (join flags, stage credentials, crew-only
   * fields too).
   */
  applyRealtimeUpdate(contentId, partial) {
    if (!contentId || !partial) return;
    const key = String(contentId);
    const existing = this.streams[key];
    if (existing && !existing.error) {
      // Only overlay defined fields so a partial push can't wipe cached values
      // (e.g. role flags / stage creds not included in the broadcast shape).
      const overlay = {};
      Object.keys(partial).forEach((k) => {
        if (partial[k] !== undefined) overlay[k] = partial[k];
      });
      this.streams[key] = { ...existing, ...overlay };
      this.emitStateUpdate();
    }
    this.scheduleReload(key);
  }

  scheduleReload(key) {
    if (this.reloadTimers[key]) return;
    this.reloadTimers[key] = setTimeout(() => {
      delete this.reloadTimers[key];
      this.load(key, this.queries[key]);
    }, 250);
  }

  /** Re-sync every known stream (e.g. after a websocket reconnect missed events). */
  reloadAll() {
    Object.keys(this.streams).forEach((key) => {
      const cached = this.streams[key];
      // `undefined` means a load is in flight — re-issue so a missed push cannot
      // leave the client stuck on the loading placeholder forever.
      if (cached === undefined || cached) {
        this.load(key, this.queries[key]);
      }
    });
  }

  clear(contentId) {
    const key = String(contentId);
    delete this.streams[key];
    delete this.loadPromises[key];
    this.camControlListeners.delete(key);
    this.emitStateUpdate();
  }

  onCamControl(contentId, handler) {
    const key = String(contentId);
    if (!this.camControlListeners.has(key)) {
      this.camControlListeners.set(key, new Set());
    }
    this.camControlListeners.get(key).add(handler);
  }

  offCamControl(contentId, handler) {
    this.camControlListeners.get(String(contentId))?.delete(handler);
  }

  /** Called by Websockets when the server relays a host cam-control signal. */
  applyCamControl(contentId, payload) {
    const listeners = this.camControlListeners.get(String(contentId));
    if (!listeners?.size) return;
    listeners.forEach((handler) => {
      try {
        handler(payload);
      } catch {
        //
      }
    });
  }
}

export default new LiveStreamSession();
